
import {timer,  Subscription ,  Observable ,  Observer,  Subject } from 'rxjs';
import {take, retryWhen, debounceTime, first, filter, map, catchError, delay} from 'rxjs/operators';
import { SignalRConnectionState, getHumanFriendlySignalRConnectionState } from './../../enum/realtime/signalr-connection-state.enum';
import { Injectable, OnDestroy } from '@angular/core';
import { SettingsService } from '../settings/settings.service';
import { ActiveQuote } from '../../models/realtime/active-quote.model';
import { hubConnection } from '../../scripts/signalr-no-jquery/signalR.js';
import { RolesUser } from '../../enum/roles-user.enum';
import { OrderSideEnum } from '../../enum/orders/side.enum';
import { RealtimeHub } from '../../enum/realtime/realtime-hub.enum';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { RealtimeHubMethod } from '../../enum/realtime/realtime-hub-method.enum';
import { RealtimeHubEvent } from '../../enum/realtime/realtime-hub-event.enum';
import { AppState } from '../../interfaces/app-state.interface';
import { Store } from '@ngrx/store';
import { UsersEffects } from '../../state-management/users/users.effects';
import { UsersAction } from '../../state-management/users/users.actions';
import { UtilitiesService } from '../utilities.service';
import { SuitabilityProfilesEnum } from '../../enum/suitability/suitability-profiles.enum';
import { Roles } from '../../models/roles/roles.enum';
import { SubscribersInfo } from './subscribersInfo.service';
import { SentryService } from './../sentry/sentry.service';
import { UserManagerService } from '../../services/users/user-manager.service';

const UNSUBSCRIBE_DELAY = 15000;

@AutoUnsubscribe()
@Injectable()
export class RealtimeService implements OnDestroy {
    public sessionIdSubscription: Subscription;
    public logoutAndLoginSubscription: Subscription;
    public userTokenSubscription: Subscription;
    public remakeSubscriptionsSubscription: Subscription;
    public authenticatedConnection: boolean;
    // private state = SignalRConnectionState.Disconnected;
    private get state(): SignalRConnectionState {
        return this.connectionReference ? this.connectionReference.state : SignalRConnectionState.Disconnected;
    }
    private set state(state: SignalRConnectionState) {
        if (this.connectionReference) {
            this.connectionReference.state = state;
        }
    }
    private firstConnectionInProgress: boolean;
    private url: string;
    private token: string;
    private realtimeToken: string;
    private role: any = null;
    private cblc: any;
    private unsubsribeSubscriptions = [];
    private connectionReference = null;
    private subscribers = {
        [RealtimeHub.Quote]: new SubscribersInfo(),
        [RealtimeHub.Tendency]: new SubscribersInfo(),
        [RealtimeHub.Research]: new SubscribersInfo(),
        [RealtimeHub.Notification]: new SubscribersInfo(),
        [RealtimeHub.Exchage]: new SubscribersInfo(),
        [RealtimeHub.Market]: new SubscribersInfo(),
        [RealtimeHub.Time]: new SubscribersInfo(),
        [RealtimeHub.Custody]: new SubscribersInfo(),
        [RealtimeHub.ConsolidatedPosition]: new SubscribersInfo(),
        [RealtimeHub.FinancePosition]: new SubscribersInfo(),
    };
    private switchOperation = false;
    private connectionStateChangedSource = new Subject<SignalRConnectionState>();
    private connectionStateChanged$ = this.connectionStateChangedSource.asObservable();
    private connectionRestabilished = false;

    // quote
    private quoteHubProxy: any;
    private quoteSubject = new Subject<any>();

    // research
    private researchHubProxy: any;
    private researchSubject = new Subject<any>();

    // tendency
    private tendencyHubProxy: any;
    private tendencySubject = new Subject<any>();

    // market
    private marketHubProxy: any;
    private marketSubject = new Subject<any>();

    // time
    private timeHubProxy: any;
    private timeSubject = new Subject<any>();

    // notification
    private notificationHubProxy: any;
    private notificationSubject = new Subject<any>();

    // exchange
    private exchangeProxy: any;
    private exchangeSubject = new Subject();

    // custody
    private custodyProxy: any;
    private custodySubject = new Subject();

    // consolidated position
    private consolidatedPositionProxy: any;
    private consolidatedPositionSubject = new Subject();

    // finance position
    private financePositionProxy: any;
    private financePositionSubject = new Subject();

    private reconnectionInProgress: boolean;
    private resetSubject = new Subject();
    private connectionStatusSubscription: Subscription;

    constructor(
        private settingsService: SettingsService,
        private store: Store<AppState>,
        private utilitiesService: UtilitiesService,
        private sentryService: SentryService,
        private UserManagerService: UserManagerService
    ) {
        this.setStoreSubscriptions();
    }

    public setStoreSubscriptions() {
        this.userTokenSubscription = this.store.select((s) => s.authentication.currentUser)
            .pipe(
                filter((currentUser) => currentUser && currentUser.token),
                map((currentUser) => ({ token: currentUser.token, role: currentUser.role, cblc: currentUser.cblc })))
            .subscribe(({ token, role, cblc }) => {
                this.token = token;
                this.role = role;
                this.cblc = cblc;
            });

        this.sessionIdSubscription = this.store.select((s) => s.authentication.sessionID)
            .pipe(
                filter((sessionId) => sessionId))
            .subscribe((sessionId) => {
                this.token = this.token || sessionId;
            });

        this.sessionIdSubscription = this.store.select((s) => s.authentication.currentUser)
            .pipe(
                filter((sessionId) => sessionId))
            .subscribe((sessionId) => {
                this.token = this.token || sessionId;
            });

        this.sessionIdSubscription = this.store.select((s) => s.userProfile.user)
            .pipe(
                filter((user) => user && user.profile ? true : false),
                map((user) => user.profile))
            .subscribe((profile) => {
                this.role = profile;
            });

        this.realtimeToken = this.UserManagerService.getUserRealtimeToken();
    }

    /**
     * Abre conexão entre o client e o server
     */
    public connect(token: string, force: boolean = true, changingConnection: boolean = false): Observable<any> {

        this.unsubsribeSubscriptions.forEach((s) => s.unsubscribe());
        this.unsubsribeSubscriptions = [];

        if (this.connectionStatusSubscription) {
            this.connectionStatusSubscription.unsubscribe();
        }

        // conexão solicitada antes de um token estar disponível (sessionId ou JWT)
        if (!token || (this.firstConnectionInProgress && !changingConnection)) {
            // aguarda até que uma conexão com um token válido seja aberta
            return new Observable((observer) => {
                this.connectionStateChanged$.pipe(first((state) => state === SignalRConnectionState.Connected))
                    .subscribe((state) => {
                        observer.next(this.connectionReference);
                        observer.complete();
                    });
            });
        }

        const authenticated = this.canUseRealTimeVersion(token);

        // conexão disponível mas apontando para um servidor diferente do solicitado (sem delay ou com delay) ou alguém solicitou a reconexão (force == true)
        if (this.connectionReference && (this.connectionReference.authenticated !== authenticated || force)) {

            this.switchOperation = this.connectionReference.authenticated !== authenticated;
            this.connectionReference.stoped = true;
            this.connectionReference.stop();
            // this.connectionReference = null;

        } else {
            if (this.connectionReference) {
                this.connectionReference.stoped = false;
            }
            // conexão disponível, mesmo servidor que o solicitado e não foi solicitada o force de reconexão
            if (this.connectionReference && this.connectionReference.authenticated === authenticated && !force) {
                return new Observable( (observer) => {
                    observer.next(this.connectionReference);
                    observer.complete();
                });
            }
        }

        this.state = SignalRConnectionState.Connecting;
        if (this.firstConnectionInProgress === undefined) {
            this.firstConnectionInProgress = true;
        }
        // abrir conexão
        return Observable.create( (observer) => {
            const urlConfigurationKey: string = authenticated ? 'sruau' : 'srauu';
            this.url = this.settingsService.getConfigurationKeyValue(urlConfigurationKey);
            this.connectionReference = hubConnection(this.url);

            this.connectionReference.authenticated = authenticated;
            this.connectionReference.stateChanged(this.connectionStateChangedHanlder.bind(this));
            // get token
            token = this.normalizeToken(token);
            this.connectionReference.qs = { access_token: 'na' };

            this.buildProxiesObservables(authenticated);
            this.connectionReference.start({
                transport: ['webSockets'],
                waitForPageLoad: false,
                authorization: token,
                realtimeToken: this.realtimeToken
            })
                .fail((error) => {
                    observer.error(error);
                });

            this.connectionStateChanged$.pipe(first((state) => state === SignalRConnectionState.Connected))
                .subscribe((state) => {
                    this.listenToConnectionStatus();
                    observer.next(this.connectionReference);
                    observer.complete();
                });
        });
    }
    /**
     * Recupera o token real para conexão com o backend (caso o usuário identificado tenha
     * sido marcado como delay a sessio Id deve ser extraída do jwt)
     */
    public normalizeToken(token: string) {

        if (this.utilitiesService.isValidJwtToken(token) && this.checkIfIsActive(this.role)) {
            return token;
        }

        if (this.utilitiesService.isValidJwtToken(token) && !this.checkIfIsActive(this.role)) {
            return this.utilitiesService.extractJwtKeyValue(token, 'SessionID');
        }

        return token;
    }

    /**
     * Refaz subscriptions de uam conexão anterior na nova conexão
     * @param data dados para realizar as inscrições
     */
    public remakeSubcriptions(data: Array<{key: string, topics: string[]}>,
                              forceImediateSubscriptions: boolean,
                              tryingToReconnect: boolean = false) {
        // ao abri conexão, refazer as inscrições anteriores
        const remake = (remakeData) => {
            // quote
            const quoteHub = remakeData.find((item) => RealtimeHub.Quote.equals(item.key));
            if (quoteHub) {
                this.subscribeActivesArrayQuote(quoteHub.topics)
                    .subscribe();
            }
            // tendency
            const tendencyHub = remakeData.find((item) => RealtimeHub.Tendency.equals(item.key));
            if (tendencyHub) {
                this.subscribeArrayTendency(tendencyHub.topics)
                    .subscribe();
            }
        };

        if (tryingToReconnect && forceImediateSubscriptions) {
            remake(data);
        } else {

            if (forceImediateSubscriptions) {
                remake(data);
            } else {
                this.unsubscribeIfNeeded(this.remakeSubscriptionsSubscription);
                this.remakeSubscriptionsSubscription = this.connectionStateChanged$.pipe(first((state) => state === SignalRConnectionState.Connected))
                    .subscribe(() => {
                        remake(data);
                    });
            }
        }
    }

    /**
     * Realiza inscricão para recebimento de cotação de um ativo
     * @param symbol código do ativo
     * @return Observable que notifica sobre novas cotações recebidas
     */
    public subscribeActiveQuote(symbol: string): Observable<ActiveQuote> {

        if (this.subscribers[RealtimeHub.Quote].hasSubscriber(symbol)) {
            // registra inscrições
            this.subscribers[RealtimeHub.Quote].registerSubscriber(symbol);
            return Observable.create( (observer: Observer<any> ) => {
                // verifica ativos que já estão recebendo atualição
                this.quoteSubject.pipe(
                    filter((body) => symbol.match(new RegExp(body.Active, 'i')) != null))
                    .subscribe(observer);
            });
        }

        // registra inscrições
        this.subscribers[RealtimeHub.Quote].registerSubscriber(symbol);
        return Observable.create( (observer) => {
            this.getConnectionStream()
                .subscribe((connection) => {
                    const active = symbol;

                    const subscription = this.quoteSubject.pipe(filter((body) => {
                        return active.match(new RegExp(body.Active, 'i')) != null;
                    }))
                        .subscribe(observer);

                    this.quoteHubProxy.invoke(RealtimeHubMethod.Subscribe, active)
                        .done()
                        .fail((error) => {
                            observer.error(error);
                        });
                });
        });
    }

    /**
     * Realiza inscrição de uma lista de ativos para receber cotações
     * @param symbols códigos dos ativos a serem assinados
     * @return Stream que notifica sobre atualização da cotação
     */
    public subscribeActivesArrayQuote(symbols: string[]): Observable<ActiveQuote> {
        const symbolsBackup = [...symbols];
        // simbolos distintos
        symbols = symbols.filter((v, i, a) => a.indexOf(v) === i);
        // simbolos que não estão registrados ainda
        symbols = symbols.filter((i) => this.subscribers[RealtimeHub.Quote].hasSubscriber(i) === false);

        return Observable.create( (observer: Observer<any> ) => {
            // registra inscrições
            this.subscribers[RealtimeHub.Quote].registerSubscribers(symbolsBackup);

            // nenhum simbolos para fazer subscribe no backend
            if (symbols.length === 0 ) {
                return this.quoteSubject.subscribe(observer);
            }

            // listen to updates
            this.quoteSubject.pipe(
                filter((body) => symbols.find((i) => i.match( new RegExp(body.Active, 'i')) != null) != null))
                .subscribe(observer);

            return this.getConnectionStream()
                .subscribe((connection) => {
                    this.quoteHubProxy.invoke(RealtimeHubMethod.SubscribeList, symbols)
                        .done()
                        .fail((error) => observer.error(error));
                });
        });
    }
    /**
     * Realiza unsubscribe para cotação de um ativo
     * @param symbol ativo que não é mais necessária a cotação
     */
    public unsubscribeActiveQuote(symbol: string): Observable<boolean> {
        return this.unsubscribeFromProxy(RealtimeHub.Quote, symbol);
    }

    /**
     * Realiza unsubscribe para cotação de ativos
     * @param symbols ativos que não são mais necessário o recebimento de cotação
     */
    public unsubscribeActiveArrayQuote(symbols: string[]): Observable<boolean> {
        return this.unsubscribeFromProxy(RealtimeHub.Quote, symbols, RealtimeHubMethod.UnsubscribeList);
    }

    /**
     * Inscrever para recomendações
     */
    public subscribeToResearchAdvices(): Observable<any> {
        // return this.subscribeToProxyData(RealtimeHub.Research);
        this.subscribers[RealtimeHub.Research].registerSubscriber();
        return Observable.create( (observer) => {
            this.getConnectionStream()
                .subscribe((connection) => {
                    this.researchSubject.subscribe(observer);
                });
        });
    }

    /**
     * Inscrever para atualizações do exchange
     */
    public subscribeToExchangeUpdates(cblc: string): Observable<any> {
        this.subscribers[RealtimeHub.Exchage].registerSubscriber(cblc);
        return Observable.create( (observer) => {
            this.getConnectionStream()
                .subscribe((connection) => {
                    this.exchangeSubject
                        .pipe(filter((body) => body != null))
                        .subscribe(observer);

                    if (!this.exchangeProxy) {
                        observer.error('Exchange Proxy not defined.');
                        return;
                    }

                    this.exchangeProxy
                        .invoke(RealtimeHubMethod.Subscribe, cblc)
                        .done()
                        .fail((error) => {
                            observer.error(error);
                        });
                });
        });
    }

    /**
     * Inscrever para atualizações do posrisk (custody)
     */
    public subscribeToCustodyUpdates(cblc: string): Observable<any> {
        this.subscribers[RealtimeHub.Custody].registerSubscriber(cblc);

        return Observable.create( (observer) => {
            this.getConnectionStream()
                .subscribe((connection) => {
                    this.custodySubject
                        .pipe(filter((body) => body != null))
                        .subscribe(observer);

                    if (!this.custodyProxy) {
                        observer.error('Exchange Proxy not defined.');
                        return;
                    }

                    this.custodyProxy
                        .invoke(RealtimeHubMethod.Subscribe, cblc)
                        .done()
                        .fail((error) => {
                            observer.error(error);
                        });
                });
        });
    }

    /**
     * Increver para receber tendência para um ativo
     * @param symbol código do ativo
     */
    public subscribeToTendency(symbol: string) {
        return this.subscribeToProxyData(RealtimeHub.Tendency, symbol);
    }

    /**
     * Increver para receber tendência para um ativo
     * @param symbol código do ativo
     */
    public subscribeArrayTendency(symbols: string[]) {
        return this.subscribeToProxyDataByArray<any>(RealtimeHub.Tendency, symbols);
    }

    /**
     * Realiza unsubscribe de uma tendencia
     * @param symbol código do ativo
     */
    public unsubscribeTendency(symbol: string): Observable<boolean> {
        return this.unsubscribeFromProxy(RealtimeHub.Tendency, symbol);
    }

    /**
     * Realiza unsubscribe de tendencia praa uma lista de ativos
     * @param symbols códigos dos ativos
     */
    public unsubscribeArrayTendency(symbols: string[]): Observable<boolean> {
        return this.unsubscribeFromProxy(RealtimeHub.Tendency, symbols, RealtimeHubMethod.UnsubscribeList);
    }

    /**
     * Realiza subscribe para recebimento de notificações
     */
    public subscribeToNotifications() {
        this.subscribers[RealtimeHub.Notification].registerSubscriber();
        return Observable.create( (observer) => {
            this.getConnectionStream()
                .subscribe((connection) => {
                    this.notificationSubject.subscribe(observer);
                });
        });
    }

    /**
     * Realiza subscribe para o status do mercado
     */
    public subscribeToMarketStatus() {
        this.subscribers[RealtimeHub.Market].registerSubscriber();
        return Observable.create( (observer) => {
            this.getConnectionStream()
                .subscribe((connection) => {
                    this.marketSubject.subscribe(observer);
                });
        });
    }

    /**
     * Realiza subscribe para atualizações de data/hora em tempo real
     */
    public subscribeToTimeInfo(): Observable<any> {
        this.subscribers[RealtimeHub.Time].registerSubscriber();
        return Observable.create( (observer) => {
            this.getConnectionStream()
                .subscribe((connection) => {
                    this.timeSubject.subscribe(observer);

                    this.timeHubProxy.invoke(RealtimeHubMethod.TimerSubscribe)
                        .done()
                        .fail((error) => {
                            observer.error(error);
                        });
                });
        });
    }

    /**
     * Realiza subscribe para atualizações de consolidated
     */
    public subscribeToConsolidatedPosition(cblc: string) {
        this.subscribers[RealtimeHub.ConsolidatedPosition].registerSubscriber(cblc);
        return Observable.create((observer) => {
            this.getConnectionStream()
                .subscribe((connection) => {
                    this.consolidatedPositionSubject.pipe(
                        filter((body) => body != null))
                        .subscribe(observer);

                    if ( !this.consolidatedPositionProxy ) {
                        observer.error('Proxy not defined.');
                        return;
                    }

                    this.consolidatedPositionProxy.invoke(RealtimeHubMethod.Subscribe, cblc)
                        .done()
                        .fail((error) => {
                            observer.error(error);
                        });
                });
        });
    }

    /**
     * Realiza subscribe para atualizações de finance
     */
    public subscribeToFinancePosition(cblc: string) {
        this.subscribers[RealtimeHub.FinancePosition].registerSubscriber(cblc);

        return Observable.create((observer) => {
            this.getConnectionStream()
                .subscribe((connection) => {
                    this.financePositionSubject.pipe(
                        filter((body) => body != null))
                        .subscribe(observer);

                    if (!this.financePositionProxy) {
                        observer.error('Proxy not defined.');
                        return;
                    }

                    this.financePositionProxy.invoke(RealtimeHubMethod.Subscribe, cblc)
                        .done()
                        .fail((error) => {
                            observer.error(error);
                        });
                });
        });

    }

    /**
     * Verifica se existe algum subscriber ativo para o hub e ativo/ativos especificados
     * @param hub hub alvo
     * @param symbol símbolo/símbolos do ativo(s)
     */
    public registerSubscribersAndCheckIfSubscriptionIsNeeded(hub: RealtimeHub, symbol?: string | string[]) {
        let result = false;
        if (!symbol || typeof symbol === 'string') {
            result = this.subscribers[hub].hasSubscriber(symbol as string);
            if (result === true) {
                this.subscribers[hub].registerSubscriber(symbol as string);
            }

            return !result;
        }
        symbol = symbol.filter((v, i, a) => a.indexOf(v) === i); // remove duplicados
        result = symbol.filter((item) => this.subscribers[hub].hasSubscriber(item) === false).length === 0;
        if (result === true) {
            this.subscribers[hub].registerSubscribers(symbol);
        }
        return !result;
    }

    /**
     * Recupera as informações sobre subscribers de um hub informado
     * @param hub hub de onde serão recuperadas as informações
     */
    public getSubscribersInfo(hub: RealtimeHub) {
        return this.subscribers[hub];
    }

    public ngOnDestroy() {
        // não remover, necessário para o funcionamento correto do @AutoUnsubscribe
    }

    private listenToConnectionStatus() {

        if (this.connectionStatusSubscription) {
            this.connectionStatusSubscription.unsubscribe();
        }

        this.connectionStatusSubscription = this.connectionStateChanged$
            .pipe(
                filter((state) => !this.reconnectionInProgress
                    && !this.firstConnectionInProgress
                    && SignalRConnectionState.Disconnected === state
                    && this.connectionReference
                    && this.connectionReference.stoped !== true),
                debounceTime(2000))
            .subscribe((state) => {
                this.reconnectionInProgress = true;

                if (this.state !== SignalRConnectionState.Reconnecting) {
                    this.connectionRestabilished = false;
                    // this.state = SignalRConnectionState.Reconnecting

                    this.reconnect()
                        .pipe(
                            retryWhen((error) => {
                                return Observable.create((observer: Observer<any>) => {
                                    error.pipe(delay(10000),
                                        take(180))
                                        .subscribe((data) => {
                                                observer.next(data);
                                            },
                                            () => {
                                                observer.error(error);
                                                this.reconnectionInProgress = false;
                                            });
                                });
                            }))
                        .subscribe((value) => {
                                this.connectionReference.stoped = false;
                                this.connectionRestabilished = true;
                                this.reconnectionInProgress = false;
                            },
                        );
                }
            });
    }

    /**
     * Verifica se o usuário pode usar realtime ou delay
     * @param token token (session id ou jwt)
     */
    private canUseRealTimeVersion(token: string): boolean {

        let authenticated = this.utilitiesService.isValidJwtToken(token) && this.checkIfIsActive(this.role);

        // usuário apenas identificado
        if (this.utilitiesService.isValidJwtToken(token) && !this.checkIfIsActive(this.role)) {
            authenticated = false;
        }

        return authenticated;
    }

    private reconnect(): Observable<boolean> {

        return new Observable((observer) => {
            const authenticated = this.canUseRealTimeVersion(this.token);
            this.switchOperation = this.connectionReference.authenticated !== authenticated;

            if (this.connectionRestabilished || this.state === SignalRConnectionState.Connected) {
                observer.next(true);
                observer.complete();
                return;
            }

            this.connectionStateChanged$.pipe(first((state) => SignalRConnectionState.Connected === state))
                .subscribe((state) => {
                    if (this.connectionRestabilished === false) {
                        observer.next(true);
                        observer.complete();
                        this.connectionRestabilished = true;
                    }
                });

            if (this.state === SignalRConnectionState.Disconnected) {
                this.buildProxiesObservables(authenticated, false, true);

                const urlConfigurationKey: string = authenticated ? 'sruau' : 'srauu';
                const expectedUrl = this.settingsService.getConfigurationKeyValue(urlConfigurationKey);
                if (this.url !== expectedUrl) {
                    this.connectionReference.stop();
                    this.connectionReference = hubConnection(expectedUrl);
                    this.buildProxiesObservables(authenticated, false, true);
                }

                this.connectionReference.start({
                    transport: ['webSockets'],
                    waitForPageLoad: false,
                })
                    .done()
                    .fail((error) => {
                        observer.error(error);
                    });
            }
        });

    }

    private monitorWSConnectionState(state, cblc) {
        let humanFriendlyStateWSState = getHumanFriendlySignalRConnectionState(state);

        if (humanFriendlyStateWSState == "Connecting") {
            humanFriendlyStateWSState = `Disconnected and ${humanFriendlyStateWSState}`;
        }

        this.sentryService.sendEvent({
            message: '[WebSocketConnection]',
            tags: {
                cblc: this.cblc,
                webSocketstatus: humanFriendlyStateWSState,
            }
        });
    }

    /**
     * Recupera o stream de conexão. Caso ele não exista a conexão é estabelecida
     * e então retornada
     */
    private getConnectionStream(): Observable<any> {
        const state = this.state;
        //this.monitorWSConnectionState(state, this.cblc);

        // tentativa de conexão em progresso
        if (this.state === SignalRConnectionState.Connecting || this.state === SignalRConnectionState.Reconnecting) {
            return Observable.create((observer) => {
                this.connectionStateChanged$
                    .pipe(first((connState) => connState === SignalRConnectionState.Connected))
                    .subscribe((connState) => {
                        observer.next(this.connectionReference);
                        observer.complete();
                    });
            });
        }

        // nenhuma coneção disponível
        if (this.connectionReference === null || this.state === SignalRConnectionState.Disconnected) {
            // this.state = SignalRConnectionState.Connecting;
            return Observable.create((mainObserver) => {
                this.connect(this.token).pipe(
                    retryWhen((error) => {
                        return Observable.create( (observer: Observer<any>) => {
                            error.pipe(delay(30000),
                                take(250),)
                                .subscribe( (data) => {
                                    observer.next(data);
                                });
                        });
                    }),
                    catchError((error) => {
                        return mainObserver.error(error);
                    }),)
                    .subscribe((connection) => {
                        mainObserver.next(connection);
                        mainObserver.complete();
                        return connection;
                    });
            });
        }
        // conexão ok
        return Observable.create((observer) => {
            observer.next(this.connectionReference);
            observer.complete();
        });
    }

    /**
     * Cria os proxies de domínio para comunicação com os hubs
     */
    private buildProxiesObservables(authenticated?: boolean, forceImediateSubscriptions = false, forceRemakeSubscriptions: boolean = false) {
        // backup previous data
        const previousSubscribersDataBackup: Array<{key: string, topics: string[]}> = [];

        if (this.switchOperation || forceRemakeSubscriptions) {
            Object.keys(this.subscribers)
                .filter((key) => this.subscribers[key].total > 0)
                .forEach((key) => previousSubscribersDataBackup.push({key, topics: this.subscribers[key].topics}));

            // reset data
            Object.keys(this.subscribers)
                .forEach((key) => this.subscribers[key].reset());
        }
        // quote
        this.quoteHubProxy = this.connectionReference.createHubProxy(RealtimeHub.Quote);
        this.bingSubjectToProxyEvent<ActiveQuote>(this.quoteHubProxy, this.quoteSubject, RealtimeHubEvent.QuoteUpdate);

        // market
        this.bingSubjectToProxyEvent<any>(this.quoteHubProxy, this.marketSubject, RealtimeHubEvent.MarketUpdate);

        // research
        this.researchHubProxy = this.connectionReference.createHubProxy(RealtimeHub.Research);
        this.bingSubjectToProxyEvent<any>(this.researchHubProxy, this.researchSubject, RealtimeHubEvent.ResearchNewRecomendation);

        // tendency
        this.tendencyHubProxy = this.connectionReference.createHubProxy(RealtimeHub.Tendency);
        this.bingSubjectToProxyEvent<any>(this.tendencyHubProxy, this.tendencySubject, RealtimeHubEvent.TendencyUpdate);

        // time
        this.timeHubProxy = this.connectionReference.createHubProxy(RealtimeHub.Time);
        this.bingSubjectToProxyEvent<any>(this.timeHubProxy, this.timeSubject, RealtimeHubEvent.TimeUpdate);

        // authenticated hubs
        if (authenticated) {
            // notification
            // this.notificationHubProxy = this.connectionReference.createHubProxy(RealtimeHub.Notification);
            // this.bingSubjectToProxyEvent<any>(this.notificationHubProxy, this.notificationSubject, RealtimeHubEvent.NewMessage);
            // this.notificationHubProxy.on(RealtimeHubEvent.NewMessage, message => {
            //     this.notificationSubject.next(message.Body);
            // });

            // exchange
            this.exchangeProxy = this.connectionReference.createHubProxy(RealtimeHub.Exchage);
            this.bingSubjectToProxyEvent<any>(this.exchangeProxy, this.exchangeSubject, RealtimeHubEvent.OrderUpdated);
            this.bingSubjectToProxyEvent<any>(this.exchangeProxy, this.exchangeSubject, RealtimeHubEvent.StrategyUpdated);

            this.consolidatedPositionProxy =  this.connectionReference.createHubProxy(RealtimeHub.ConsolidatedPosition);
            this.bingSubjectToProxyEvent<any>(
                this.consolidatedPositionProxy,
                this.consolidatedPositionSubject,
                RealtimeHubEvent.ConsolidatedPositionUpdated);

            this.financePositionProxy =  this.connectionReference.createHubProxy(RealtimeHub.FinancePosition);
            this.bingSubjectToProxyEvent<any>(this.financePositionProxy, this.financePositionSubject, RealtimeHubEvent.FinancePositionUpdated);

            this.custodyProxy =  this.connectionReference.createHubProxy(RealtimeHub.Custody);
            this.bingSubjectToProxyEvent<any>(this.custodyProxy, this.custodySubject, RealtimeHubEvent.CustodyUpdated);
        }

        if (previousSubscribersDataBackup.length) {
            this.remakeSubcriptions(previousSubscribersDataBackup, forceImediateSubscriptions);
        }
    }

    /**
     * Passa a ouvir um evento no proxy (canal) para passar para o subject.
     * @param proxy Canal do SignalR.
     * @param subject Subject do proxy (que o usuário vai se inscrever).
     * @param event Evento qa ser inscrito.
     */
    private bingSubjectToProxyEvent<T>(proxy: any, subject: Subject<T>, event: RealtimeHubEvent): void {
        proxy.on(event, (message) => {
            if (message.Body) {
                subject.next(message.Body);
            }
        });
    }

    /**
     * Verifica se o usuário é ativo ou cadastrado (apenas usuários ativos ou cadatrados têm acesso as informações sem delay)
     * @param profile perfil do usuário (role)
     * @return booleano se ativou ou registrado
     */

    private checkIfIsActive(profile: string | number): boolean {
        switch (profile) {
            case RolesUser.Activated:
            case Roles.Activated:
                return true;
            default:
                return false;
        }
    }

    private connectionStateChangedHanlder(state: {newState: SignalRConnectionState, oldState: SignalRConnectionState }) {

        if (this.firstConnectionInProgress && state.newState === SignalRConnectionState.Connected) {
            this.firstConnectionInProgress = false;
        }

        this.connectionStateChangedSource.next(state.newState);
        const stateConversion = {0: 'connecting', 1: 'connected', 2: 'reconnecting', 4: 'disconnected'};

        if ( state.oldState === SignalRConnectionState.Reconnecting && state.newState === SignalRConnectionState.Connected) {
            const previousSubscribersDataBackup: Array<{key: string, topics: string[]}> = [];

            Object.keys(this.subscribers)
                .filter((key) => this.subscribers[key].total > 0)
                .forEach((key) => previousSubscribersDataBackup.push({key, topics: this.subscribers[key].topics}));

            Object.keys(this.subscribers)
                .forEach((key) => this.subscribers[key].reset());

            this.remakeSubcriptions(previousSubscribersDataBackup, true, true );
        }
    }

    /**
     * Método genérico para realizar unsubscribe de um proxy
     * @param hub hub para desinscrição
     * @param symbol simbolo/simbolos para realizar a desinscrição
     * @param unsubscribeMethod método a ser invocado para desinscrição
     */
    private unsubscribeFromProxy(
        hub: RealtimeHub,
        symbol: string | string[],
        unsubscribeMethod: RealtimeHubMethod = RealtimeHubMethod.Unsubscribe,
    ): Observable<boolean> {

        const proxy = this.getProxyByHub(hub);

        return Observable.create((observer) => {
            // remove from list
            if (typeof symbol === 'string') {
                this.subscribers[hub].removeOneSubscriber(symbol);
            } else {
                symbol.forEach((s) => this.subscribers[hub].removeOneSubscriber(s));
            }

            // check if shoud really unsubscribe after delay
            let anySymbolToUnsubscribe = true;
            if (typeof symbol === 'string') {
                anySymbolToUnsubscribe = this.subscribers[hub].getSubscribersAmount(symbol) === 0;
            } else {
                symbol = symbol.filter((s) => this.subscribers[hub].getSubscribersAmount(s) === 0);
                anySymbolToUnsubscribe = symbol.length !== 0;
            }

            // unsubscribe if any symbol to unsubscribe
            if (anySymbolToUnsubscribe) {
                // função para desinscrever no proxy
                const unsubscribe = () => {
                    proxy.invoke(unsubscribeMethod, symbol)
                        .then((s) => {
                            observer.next(true);
                            observer.complete();
                        })
                        .fail((s) => {
                            observer.next(false);
                            observer.complete();
                        });
                };

                // se há uma conexão disponível, unsubscribe via proxy
                if (this.connectionReference && this.connectionReference.state === SignalRConnectionState.Connected) {
                    unsubscribe();
                } else {// espera uma conexã disponível então realiza unsubscribe via proxy
                    this.connectionStateChanged$.pipe(first((state) => state === SignalRConnectionState.Connected))
                        .subscribe((state) => {
                            unsubscribe();
                        });
                }
            } else {
                observer.next(true);
                observer.complete();
            }
        });
    }

    /**
     * Método genérico para se inscrever e receber dados do proxy
     * @param hub hub para inscrição
     * @param symbol símbolos para inscrição
     * @param filterPredicate predicate para aplicar filtro ao stream de dados
     */
    private subscribeToProxyData<T>(hub: RealtimeHub, symbol?: string, filterPredicate?: (body) => boolean): Observable<T> {

        const proxySubject = this.getProxySubjectByHub(hub);
        let filter = (body) => true;
        if (symbol) {
            filter = filterPredicate ? (body) => filterPredicate(body) : (body) => symbol.match(new RegExp(body.Active, 'i')) != null;
        }

        // verifica se já existe um proxy trabalhando no sentido esperado
        if (this.subscribers[hub].hasSubscriber(symbol)) {
            this.subscribers[hub].registerSubscriber(symbol);
            return Observable.create( (observer: Observer<any> ) => {
                //TODO: verificar funcionamento do subscribeToProxy
                proxySubject
                    // .pipe(
                    //   filter(filter)
                    // )
                    .subscribe(observer);
            });
        }

        this.subscribers[hub].registerSubscriber(symbol);
        return Observable.create((observer) => {
            this.getConnectionStream()
                .subscribe((connection) => {
                    //TODO: verificar funcionamento do subscribeToProxy
                    const subscription = proxySubject
                        // .pipe(
                        //   filter(filter)
                        // )
                        .subscribe(observer);

                    const proxyReference = this.getProxyByHub(hub);

                    if (symbol) {
                        proxyReference.invoke(RealtimeHubMethod.Subscribe, symbol)
                            .fail((error) => {
                                observer.error(error);
                            });
                    } else {
                        proxyReference.invoke(RealtimeHubMethod.Subscribe)
                            .fail((error) => {
                                observer.error(error);
                            });
                    }

                    return connection;
                });
        });
    }

    /**
     * Método genérico para se inscrever e receber dados do proxy usando um array de símbolos
     * @param hub hub alvo
     * @param proxySubject
     * @param filterPredicate predicate para aplicar filtro ao stream de dados
     */
    private subscribeToProxyDataByArray<T>(hub: RealtimeHub, symbols: string[], filterPredicate?: (body) => boolean): Observable<T> {
        const symbolsBackup = [...symbols];

        const filter = filterPredicate
            ? (body) => filterPredicate(body)
            : (body) => symbols.find((i) => i.match(new RegExp(body.Active, 'i')) != null) != null;

        const proxySubject = this.getProxySubjectByHub(hub);

        // simbolos distintos
        symbols = symbols.filter((v, i, a) => a.indexOf(v) === i);
        // simbolos que não estão registrados ainda
        symbols = symbols.filter((i) => this.subscribers[hub].hasSubscriber(i) === false);

        // verifica se já existe um proxy trabalhando no sentido esperado
        if (symbols.length === 0) {
            this.subscribers[hub].registerSubscribers(symbolsBackup); // registrar inscrições
            return Observable.create( (observer: Observer<any> ) => {
                proxySubject
                    // TODO: verificar funcionamento do subscribeToProxyByArray
                    // .filter(filter)
                    .subscribe(observer);
            });
        }

        this.subscribers[hub].registerSubscribers(symbolsBackup); // registrar inscrições
        return Observable.create( (observer) => {
            this.getConnectionStream()
                .subscribe((connection) => {

                    const subscription = proxySubject
                        // TODO: verificar funcionamento do subscribeToProxy
                        // .filter(filter)
                        .subscribe(observer);

                    const proxyReference = this.getProxyByHub(hub);

                    proxyReference.invoke(RealtimeHubMethod.SubscribeList, symbols)
                        .fail((error) => {
                            observer.error(error);
                        });
                });
        });
    }

    /**
     * Retorna um proxy a partir de um hub
     * @param hub hub
     */
    private getProxyByHub(hub: RealtimeHub) {
        switch (hub) {
            case RealtimeHub.Quote:
                return this.quoteHubProxy;
            case RealtimeHub.Notification:
                return this.notificationHubProxy;
            case RealtimeHub.Research:
                return this.researchHubProxy;
            case RealtimeHub.Tendency:
                return this.tendencyHubProxy;
            default:
                return this.quoteHubProxy;
        }
    }

    /**
     * Retorna um proxy a partir de um hub
     * @param hub hub
     */
    private getProxySubjectByHub(hub: RealtimeHub) {
        switch (hub) {
            case RealtimeHub.Quote:
                return this.quoteSubject;
            case RealtimeHub.Notification:
                return this.notificationSubject;
            case RealtimeHub.Research:
                return this.researchSubject;
            case RealtimeHub.Tendency:
                return this.tendencySubject;
            default:
                return this.quoteSubject;

        }
    }
    /**
     * Veifica se é necessário realizar unsubscribe
     * @param subscription subscriptions para verificação
     */
    private unsubscribeIfNeeded(subscription: Subscription) {
        if (subscription && !subscription.closed) {
            subscription.unsubscribe();
        }
    }
}
